import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/auth'

/**
 * Global keyboard shortcuts for the app shell.
 *
 * Navigation shortcuts use a "g then X" pattern: pressing 'g' starts a
 * 500ms window during which the next key determines the destination.
 * Shortcuts are suppressed when the user is typing in an input, textarea,
 * or contenteditable element.
 */

function isTyping(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if ((el as HTMLElement).isContentEditable) return true
  return false
}

export function useKeyboardShortcuts({
  onShowHelp,
}: {
  onShowHelp: () => void
}) {
  const navigate = useNavigate()
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const gPendingRef = useRef(false)
  const gTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const clearGPending = useCallback(() => {
    gPendingRef.current = false
    if (gTimerRef.current) {
      clearTimeout(gTimerRef.current)
      gTimerRef.current = undefined
    }
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Never interfere with modifier combos (Cmd+K is handled by CommandPalette)
      if (e.metaKey || e.ctrlKey || e.altKey) return

      // Escape — close the most recent overlay. We dispatch a custom event
      // so overlays can handle it in priority order. If nothing catches it,
      // no harm done.
      if (e.key === 'Escape') {
        // Let overlays handle Escape via their own listeners — don't prevent
        // default here so existing Escape handlers (CommandPalette, lightbox,
        // etc.) continue to work. The shortcuts help overlay listens for this
        // key directly.
        return
      }

      if (isTyping()) return

      // '?' — show keyboard shortcuts help. Checked before '/' because on
      // layouts where '?' is Shift+'/' the event can arrive as key '/' with
      // shiftKey set, and the '/' branch used to swallow it (CLA-1257).
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault()
        onShowHelp()
        return
      }

      // '/' — focus search input on current page
      if (e.key === '/') {
        const searchInput = document.querySelector<HTMLInputElement>(
          'input[type="text"][placeholder*="earch"]'
        )
        if (searchInput) {
          e.preventDefault()
          searchInput.focus()
        }
        return
      }

      // 'g' — start navigation sequence
      if (e.key === 'g' && !gPendingRef.current) {
        gPendingRef.current = true
        gTimerRef.current = setTimeout(clearGPending, 500)
        return
      }

      // Second key after 'g'
      if (gPendingRef.current) {
        clearGPending()
        switch (e.key) {
          case 'n':
            e.preventDefault()
            navigate('/')
            return
          case 'p':
            e.preventDefault()
            navigate('/patients')
            return
          case 's':
            e.preventDefault()
            navigate('/settings')
            return
          case 'c':
            e.preventDefault()
            navigate('/calendar')
            return
          case 'm':
            e.preventDefault()
            navigate('/messaging')
            return
          case 'i':
            e.preventDefault()
            navigate('/inbox')
            return
          case 'o':
            e.preventDefault()
            navigate('/order')
            return
          case 'a':
            if (isAdmin) {
              e.preventDefault()
              navigate('/manage')
            }
            return
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      clearGPending()
    }
  }, [navigate, isAdmin, onShowHelp, clearGPending])
}
